export class PerspectiveTransform {
    private transform: (x: number, y: number) => [number, number];

    private transformInverse: (x: number, y: number) => [number, number];

    constructor(srcPoints: number[][], destPoints: number[][]) {
        const { transform, transformInverse } = this.createPerspectiveTransform(srcPoints, destPoints);
        this.transform = transform;
        this.transformInverse = transformInverse;
    }

    /**
     * Custom perspective transform implementation
     * @param sourcePoints Array of 8 values [x0,y0, x1,y1, x2,y2, x3,y3]
     * @param destPoints Array of 8 values for destination quadrilateral
     */
    private createPerspectiveTransform = (sourcePoints: number[][], destPoints: number[][]) => {
        // Compute the perspective transform matrix
        const matrix = this.computePerspectiveMatrix(sourcePoints, destPoints);
        const inverseMatrix = this.computePerspectiveMatrix(destPoints, sourcePoints);

        return {
            transform: (x: number, y: number): [number, number] => {
                const [X, Y, W] = this.multiplyMatrixPoint(matrix, [x, y, 1]);
                return [X / W, Y / W];
            },
            transformInverse: (x: number, y: number): [number, number] => {
                const [X, Y, W] = this.multiplyMatrixPoint(inverseMatrix, [x, y, 1]);
                return [X / W, Y / W];
            },
        };
    };

    /**
     * Computes a perspective transformation matrix mapping points from source to destination
     * @param src Array of 4 points [x,y] from source quadrilateral
     * @param dst Array of 4 points [x,y] for destination quadrilateral
     * @returns 3x3 perspective transformation matrix
     */
    private computePerspectiveMatrix = (src: number[][], dst: number[][]): number[][] => {
        // Set up system of linear equations
        const A: number[][] = [];
        const b: number[] = [];

        // For each corner point
        for (let i = 0; i < 4; i++) {
            const [x, y] = src[i];
            const [u, v] = dst[i];

            // Add equations for x coordinate
            A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
            b.push(u);

            // Add equations for y coordinate
            A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
            b.push(v);
        }

        // Solve the system A*h = b
        const h = this.solveLinearSystem(A, b);

        // Construct the transformation matrix
        return [
            [h[0], h[1], h[2]],
            [h[3], h[4], h[5]],
            [h[6], h[7], 1],
        ];
    };

    /**
     * Multiplies a 3x3 matrix by a point [x,y,1]
     * @param matrix 3x3 transformation matrix
     * @param point 3D vector in homogeneous coordinates [x,y,w]
     * @returns Transformed point [x',y',w']
     */
    private multiplyMatrixPoint = (matrix: number[][], point: number[]): number[] => {
        const result = [0, 0, 0];

        for (let i = 0; i < 3; i++) {
            result[i] = matrix[i][0] * point[0] + matrix[i][1] * point[1] + matrix[i][2] * point[2];
        }

        return result;
    };

    /**
     * Solves a system of linear equations using Gaussian elimination
     * @param A Coefficient matrix
     * @param b Right-hand side vector
     * @returns Solution vector
     */
    private solveLinearSystem = (A: number[][], b: number[]): number[] => {
        const n = b.length;
        const augMatrix: number[][] = [];

        // Create augmented matrix [A|b]
        for (let i = 0; i < n; i++) {
            augMatrix.push([...A[i], b[i]]);
        }

        // Forward elimination
        for (let i = 0; i < n; i++) {
            // Find pivot (maximum value in column i)
            let maxRow = i;
            let maxVal = Math.abs(augMatrix[i][i]);

            for (let j = i + 1; j < n; j++) {
                const absVal = Math.abs(augMatrix[j][i]);
                if (absVal > maxVal) {
                    maxVal = absVal;
                    maxRow = j;
                }
            }

            // Swap rows if needed (pivot strategy)
            if (maxRow !== i) {
                [augMatrix[i], augMatrix[maxRow]] = [augMatrix[maxRow], augMatrix[i]];
            }

            // Eliminate below
            for (let j = i + 1; j < n; j++) {
                const factor = augMatrix[j][i] / augMatrix[i][i];

                for (let k = i; k <= n; k++) {
                    augMatrix[j][k] -= factor * augMatrix[i][k];
                }
            }
        }

        // Back substitution
        const x = new Array(n).fill(0);

        for (let i = n - 1; i >= 0; i--) {
            let sum = 0;

            for (let j = i + 1; j < n; j++) {
                sum += augMatrix[i][j] * x[j];
            }

            x[i] = (augMatrix[i][n] - sum) / augMatrix[i][i];
        }

        return x;
    };

    public map = (x: number, y: number): [number, number] => this.transform(x, y);

    public mapInverse = (x: number, y: number): [number, number] => this.transformInverse(x, y);
}
